import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { Plus, Edit2, Trash2, Users, GripVertical, Loader2 } from "lucide-react";
import { supabase } from "../../../../supabase/supabase";
import { useAuth } from "../../../../supabase/auth";
import { useParams } from "react-router-dom";

interface SubAgent {
  id: string;
  parent_assistant_id: string;
  title: string;
  description: string | null;
  position: number;
  created_at: string;
  updated_at: string;
  configuration: any;
}

interface SubAgentsTabProps {
  assistantId?: string;
}

export default function SubAgentsTab({ assistantId }: SubAgentsTabProps) {
  const { user } = useAuth();
  const params = useParams();
  const effectiveAssistantId = assistantId || params.id;
  
  const [subAgents, setSubAgents] = useState<SubAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState<SubAgent | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (effectiveAssistantId) {
      fetchSubAgents();
    }
  }, [effectiveAssistantId]);

  const fetchSubAgents = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("sub_agents")
        .select("*")
        .eq("parent_assistant_id", effectiveAssistantId)
        .order("position", { ascending: true });

      if (error) throw error;
      setSubAgents(data || []);
    } catch (error: any) {
      console.error("Error fetching sub-agents:", error);
      toast({
        title: "Error",
        description: "Failed to load sub-agents",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({ title: "", description: "" });
    setShowAddDialog(true);
  };

  const handleEdit = (agent: SubAgent) => {
    setEditingAgent(agent);
    setFormData({
      title: agent.title,
      description: agent.description || "",
    });
    setShowEditDialog(true);
  };

  const handleSubmitAdd = async () => {
    if (!formData.title.trim()) {
      toast({
        title: "Validation Error",
        description: "Title is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const newPosition = subAgents.length;
      
      const { error } = await supabase.from("sub_agents").insert({
        parent_assistant_id: effectiveAssistantId,
        title: formData.title,
        description: formData.description || null,
        position: newPosition,
        created_by: user?.id,
        updated_by: user?.id,
        configuration: {},
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sub-agent added successfully",
      });

      setShowAddDialog(false);
      fetchSubAgents();
    } catch (error: any) {
      console.error("Error adding sub-agent:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add sub-agent",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!formData.title.trim() || !editingAgent) {
      toast({
        title: "Validation Error",
        description: "Title is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("sub_agents")
        .update({
          title: formData.title,
          description: formData.description || null,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingAgent.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sub-agent updated successfully",
      });

      setShowEditDialog(false);
      setEditingAgent(null);
      fetchSubAgents();
    } catch (error: any) {
      console.error("Error updating sub-agent:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update sub-agent",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (agentId: string) => {
    if (!confirm("Are you sure you want to delete this sub-agent?")) {
      return;
    }

    try {
      const { error } = await supabase
        .from("sub_agents")
        .delete()
        .eq("id", agentId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sub-agent deleted successfully",
      });

      fetchSubAgents();
    } catch (error: any) {
      console.error("Error deleting sub-agent:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete sub-agent",
        variant: "destructive",
      });
    }
  };

  const moveAgent = async (agentId: string, direction: "up" | "down") => {
    const index = subAgents.findIndex(a => a.id === agentId);
    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= subAgents.length) return;

    const updatedAgents = [...subAgents];
    const [movedAgent] = updatedAgents.splice(index, 1);
    updatedAgents.splice(newIndex, 0, movedAgent);

    try {
      const updates = updatedAgents.map((agent, idx) => 
        supabase
          .from("sub_agents")
          .update({ position: idx })
          .eq("id", agent.id)
      );

      await Promise.all(updates);
      setSubAgents(updatedAgents);
      
      toast({
        title: "Success",
        description: "Sub-agent order updated",
      });
    } catch (error) {
      console.error("Error reordering sub-agents:", error);
      toast({
        title: "Error",
        description: "Failed to reorder sub-agents",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Sub-Agents</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Manage the sub-agents that work together in this multi-agent assistant
              </p>
            </div>
            <Button onClick={handleAdd} size="sm" type="button">
              <Plus className="h-4 w-4 mr-2" />
              Add Sub-Agent
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {subAgents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No sub-agents yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add sub-agents to create a multi-agent system
              </p>
              <Button onClick={handleAdd} variant="outline" type="button">
                <Plus className="h-4 w-4 mr-2" />
                Add First Sub-Agent
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {subAgents.map((agent, index) => (
                <Card key={agent.id} className="relative">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="flex flex-col gap-1 mt-1">
                          <button
                            onClick={() => moveAgent(agent.id, "up")}
                            disabled={index === 0}
                            className="p-1 hover:bg-muted rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <GripVertical className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => moveAgent(agent.id, "down")}
                            disabled={index === subAgents.length - 1}
                            className="p-1 hover:bg-muted rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <GripVertical className="h-4 w-4 rotate-180" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-base mb-1">
                            {agent.title}
                          </h4>
                          {agent.description && (
                            <p className="text-sm text-muted-foreground">
                              {agent.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Position: {index + 1}</span>
                            <span>Created: {new Date(agent.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleEdit(agent)}
                          size="sm"
                          variant="outline"
                          type="button"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(agent.id)}
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                          type="button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Sub-Agent Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Sub-Agent</DialogTitle>
            <DialogDescription>
              Create a new sub-agent for this multi-agent assistant
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title*</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Customer Service Agent"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the role and capabilities of this sub-agent..."
                rows={4}
                className="max-h-[60vh] overflow-y-auto"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitAdd} disabled={isSubmitting} type="button">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Sub-Agent"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sub-Agent Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sub-Agent</DialogTitle>
            <DialogDescription>
              Update the sub-agent information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title*</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Customer Service Agent"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the role and capabilities of this sub-agent..."
                rows={4}
                className="max-h-[60vh] overflow-y-auto"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitEdit} disabled={isSubmitting} type="button">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}